/* eslint-disable no-return-assign */
import { createSelector } from 'reselect';
import utils from 'utils';
import { extrasSelector } from 'selectors/extrasSelectors';
import { RESERVATIONS } from 'constants';

const STATUSES = [
  RESERVATIONS.EXTRAS_INCLUDED_STATUS,
  RESERVATIONS.EXTRAS_OPTIONAL_STATUS,
  RESERVATIONS.EXTRAS_MANDATORY_STATUS,
  RESERVATIONS.EXTRAS_WAIVED_STATUS,
  RESERVATIONS.EXTRAS_ADDITIONAL_STATUS,
];

/* Utility Functions */
const getPolicy = (dataSource = [], key, status) => dataSource.filter(s => s[key] === status);

// NOTE: May be best to switch to selector below
const filterExtrasByStatus = (extras = []) => {
  const filteredObject = {};
  STATUSES.forEach(status => {
    filteredObject[status] = extras.filter(extra => extra.status === status);
  });
  return filteredObject;
};

const filterExclusionsByStatus = (keyFactsPolicies, insurance) => {
  const filteredObject = {};
  STATUSES.map(
    status =>
      (filteredObject[status] = keyFactsPolicies.filter(keyFact => {
        const policyCode = insurance[status].map(p => p.lrd_policy_code);
        return policyCode.some(f => f === keyFact.code);
      }))
  );
  return filteredObject;
};

const mapContentWithExclusions = (policies, exclusions, key) => {
  const policiesWithExclusions = policies.map(item => {
    const extra = { ...item};
    const exclusion = exclusions[key].find(exc => item.lrd_policy_code === exc.code);
    if (exclusion) {
      const policyExclusion = exclusion.policy_exclusions?.[0] || {};
      const { description = '', policy_text = '' } = policyExclusion;
      extra.detailed_description = `${item.detailed_description}
        <div class='policy-exclusion--description'>${description}</div>
        <div class='policy-exclusion--policy'>${policy_text}</div>
      `;
    }
    return extra;
  });
  return policiesWithExclusions;
};

/* Content Generators */
/* TODO: This selector generates a lot of static content which isn't appropriate here. May need to refactor
 *  this selector and the component that uses it to split the static content off into the component
 */
const getBaseContent = () => `
  <p>${utils.i18n('key_facts_general_description_paragraph_1')}</p>
  <p>${utils.i18n('key_facts_general_description_paragraph_2')}</p>
  <p>${utils.i18n('key_facts_general_description_paragraph_3')}</p>
  <p>${utils.i18n('key_facts_general_description_paragraph_4')}</p>
`;

const getKeyFactsLiabilitiesContent = () => `
      <ul class="additional-bullet-list">
        <li>${utils.i18n('key_facts_additional_changes_and_liabilities_bullet_1')}</li>
        <li>${utils.i18n('key_facts_additional_changes_and_liabilities_bullet_2')}</li>
        <li>${utils.i18n('key_facts_additional_changes_and_liabilities_bullet_3')}</li>
        <li>${utils.i18n('key_facts_additional_changes_and_liabilities_bullet_4')}</li>
        <li>${utils.i18n('key_facts_additional_changes_and_liabilities_bullet_5')}</li>
        <li>${utils.i18n('key_facts_additional_changes_and_liabilities_bullet_6')}</li>
        <li>${utils.i18n('key_facts_additional_changes_and_liabilities_bullet_7')}</li>
      </ul>
      <p>${utils.i18n('key_facts_additional_changes_and_liabilities_paragraph')}</p>
    `;

const getKeyFactsReturnAndDamageContent = () => `
      <p>${utils.i18n('key_facts_vehicle_return_and_damage_paragraph_1')}</p>
      <p>${utils.i18n('key_facts_vehicle_return_and_damage_paragraph_2')}</p>
    `;

const getKeyFactsPolicies = state => state.getIn(['gmi', 'session', 'gbo', 'reservation', 'key_facts_policies']);

export const getKeyFactStaticContentSelector = createSelector(
  [getKeyFactsPolicies, extrasSelector],
  (keyFactsPolicies, extras) => {
    keyFactsPolicies = utils.safeToJS(keyFactsPolicies, []);
    const insurance = filterExtrasByStatus(extras?.insurance);
    const equipment = filterExtrasByStatus(extras?.equipment);
    const fuel = filterExtrasByStatus(extras?.fuel);
    // add fuel items to the equipment object where appropriate
    if (fuel && STATUSES.some(k => !!fuel[k].length)) {
      STATUSES.forEach(k => {
        equipment[k] = equipment[k]?.concat(fuel[k]);
      });
    }

    const baseContent = getBaseContent();

    // Protection Products
    const exclusions = filterExclusionsByStatus(keyFactsPolicies, insurance) || {};
    const joinedExclusions = STATUSES.reduce((acc, stat) => [...acc, ...exclusions[stat]], []);
    let protectionProductsIncluded = utils.array.sortByKey(insurance[RESERVATIONS.EXTRAS_INCLUDED_STATUS], 'code');
    let protectionProductsOptional = utils.array.sortByKey(insurance[RESERVATIONS.EXTRAS_OPTIONAL_STATUS], 'code');
    protectionProductsIncluded = mapContentWithExclusions(
      protectionProductsIncluded,
      exclusions,
      RESERVATIONS.EXTRAS_INCLUDED_STATUS
    );
    protectionProductsOptional = mapContentWithExclusions(
      protectionProductsOptional,
      exclusions,
      RESERVATIONS.EXTRAS_OPTIONAL_STATUS
    );

    // Equipment Products
    const equipmentProductsIncluded = utils.array.sortByKey(equipment[RESERVATIONS.EXTRAS_INCLUDED_STATUS], 'code');
    const equipmentProductsOptional = utils.array.sortByKey(equipment[RESERVATIONS.EXTRAS_OPTIONAL_STATUS], 'code');

    // Minimum Rental requirements
    const minReqPolicy = getPolicy(
      keyFactsPolicies,
      RESERVATIONS.KEY_FACTS_SECTION_KEY,
      RESERVATIONS.MINIMUM_REQUIREMENTS
    );

    // Additional Rental Policies
    const additionalPolicy = getPolicy(
      keyFactsPolicies,
      RESERVATIONS.KEY_FACTS_SECTION_KEY,
      RESERVATIONS.EXTRAS_ADDITIONAL_STATUS
    );

    // Additional Charges & Liabilities
    const additionalLiability = getKeyFactsLiabilitiesContent();

    // Vehicle Return & Damages
    const vehicleReturnPolicy = getKeyFactsReturnAndDamageContent();

    // Static Content obj
    /* TODO: This selector generates a lot of static content which isn't appropriate here. May need to refactor
     *  this selector and the component that uses it to split the static content off into the component
     */
    const staticContent = {
      modalBaseContent: {
        title: utils.i18n('key_facts_modal_title'),
        content: baseContent,
      },
      protectionStaticContent: {
        title: utils.i18n('key_facts_protection_products_title'),
        items: [
          {
            // included items
            subTitle: utils.i18n('key_facts_protection_products_subtitle_included'),
            items: protectionProductsIncluded,
            exclusions: exclusions[RESERVATIONS.EXTRAS_INCLUDED_STATUS],
          },
          {
            // optional items
            subTitle:
              protectionProductsIncluded.length > 0
                ? utils.i18n('key_facts_protection_products_subtitle_optional_additional')
                : utils.i18n('key_facts_protection_products_subtitle_optional_none_included'),
            items: protectionProductsOptional,
            exclusions: exclusions[RESERVATIONS.EXTRAS_OPTIONAL_STATUS],
          },
        ],
      },
      equipmentStaticContent: {
        title: utils.i18n('key_facts_equipment_products_title'),
        items: [
          {
            // included items
            subTitle: utils.i18n('key_facts_equipment_products_subtitle_included'),
            items: equipmentProductsIncluded,
          },
          {
            // optional items
            subTitle:
              equipmentProductsIncluded.length > 0
                ? utils.i18n('key_facts_equipment_products_subtitle_optional_additional')
                : utils.i18n('key_facts_equipment_products_subtitle_optional_none_included'),
            items: equipmentProductsOptional,
          },
        ],
      },
      minimumRequirementsStaticContent: {
        title: utils.i18n('key_facts_min_required_policies_title'),
        subTitle: utils.i18n('key_facts_min_required_policies_subtitle'),
        items: minReqPolicy,
      },
      additionalPoliciesStaticContent: {
        title: utils.i18n('key_facts_additional_policies_title'),
        subTitle: utils.i18n('key_facts_additional_policies_subtitle'),
        items: additionalPolicy,
      },
      additionalLiabilitiesStaticContent: {
        title: utils.i18n('key_facts_additional_changes_and_liabilities_title'),
        subTitle: utils.i18n('key_facts_additional_changes_and_liabilities_subtitle'),
        content: additionalLiability,
      },
      vehicleReturnAndDamagesStaticContent: {
        title: utils.i18n('key_facts_vehicle_return_and_damage_title'),
        content: vehicleReturnPolicy,
      },
      rulesOfTheRoadStaticContent: {
        title: utils.i18n('key_facts_rules_of_the_road_title'),
        subTitle: utils.i18n('key_facts_rules_of_the_road_subtitle'),
        content: utils.i18n('key_facts_rules_of_the_road_link_text'),
      },
    };

    return {
      staticContent,
      keyFactsPolicies,
      addOnExclusions: joinedExclusions,
    };
  }
);
